import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import StethoscopeLoading from "../../../components/StethoscopeLoading/StethoscopeLoading";
import { FaEdit, FaTrash } from "react-icons/fa";
import { FaHeartbeat, FaHeart, FaTint, FaDatabase, FaUnderline } from "react-icons/fa";
import { MdCalendarToday } from "react-icons/md";
import { useDispatch, useSelector } from 'react-redux';
import { getMetabolicsHistory, getVitalsHistory } from '../../../apiServices/reducer/apiService';
import { deleteMetabolicsRecord, deleteVitalRecord } from '../../../apiServices/reducer/dialogApiService';
import { showPmrDeleteConfirm } from '../../../utils/swalUtils';
import VitalsMetabolicsModal from '../../widgets/addVitals';
import '../../../styles/user.css';
import CustomSuccessModal from '../../../components/CustomSuccessModal';

const iconHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 600,
  fontSize: 12,
  color: "#003366",
  marginBottom: 0,
};

const iconCircleStyle = {
  background: "#e6f0ed",
  borderRadius: "50%",
  width: 45,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 4px auto",
};

const actionBtnStyle = (bg, color) => ({
  background: bg,
  color: color,
  border: "none",
  borderRadius: "50%",
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  margin: "0 auto",
  cursor: "pointer",
});

const hasValue = (value) => value !== null && value !== undefined && value !== "" && value !== "NA";

const formatHistoryDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const ViewHistory = ({ show, handleClose, onEdit, onEditMetabolics, onDelete, selectedFamilyMember, historyType = "vitals" }) => {
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isVitalsEdit, setIsVitalsEdit] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const dispatch = useDispatch();
  const token = useSelector((state) => state.apiService?.token);
  const userData = useSelector((state) => state.apiService?.login);
  const userId = selectedFamilyMember?.user_id || userData?.user_id;
  const isVitalsHistory = historyType === "vitals";
  const modalTitle = isVitalsHistory ? "Vitals History" : "Metabolics History";
  const filteredHistory = vitalsHistory.filter((row) => {
    if (isVitalsHistory) {
      return hasValue(row.BloodPressure_Systolic) || hasValue(row.BloodPressure_Diastolic) || hasValue(row.HeartRate);
    }

    return hasValue(row.BloodSugar) || hasValue(row.Weight) || hasValue(row.WaistSize);
  });

  const handleEditVitals = (row) => {
    const editData = {
      ...row,
      isVitals: true,
      BloodPressure_Systolic: row.BloodPressure_Systolic,
      BloodPressure_Diastolic: row.BloodPressure_Diastolic,
      HeartRate: row.HeartRate,
      LastUpdated: row.LastUpdated,
      VitalID: row.VitalID
    };
    setEditData(editData);
    setIsVitalsEdit(true);
    setShowEditModal(true);
  };

  const handleEditMetabolics = (row) => {
    const editData = {
      ...row,
      isVitals: false,
      BloodSugar: row.BloodSugar,
      Weight: row.Weight,
      WaistSize: row.WaistSize,
      LastUpdated: row.LastUpdated,
      VitalID: row.VitalID
    };
    setEditData(editData);
    setIsVitalsEdit(false);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditData(null);
    setLoading(true);
    fetchVitalsData();
  };

  const handleDelete = async (row) => {
    showPmrDeleteConfirm({
      html: `<div class="pmr-popup-message">You want to delete this ${isVitalsHistory ? 'vitals' : 'metabolics'} record? This action cannot be undone.</div>`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deleteAction = isVitalsHistory ? deleteVitalRecord : deleteMetabolicsRecord;
          await dispatch(deleteAction({
            VitalID: row.VitalID,
            LoginUserID: userId,
            token: token
          })).unwrap();

          setSuccessMessage(`${isVitalsHistory ? 'Vitals' : 'Metabolics'} Record deleted successfully!`);
          setShowSuccessModal(true);
          const getHistoryAction = isVitalsHistory ? getVitalsHistory : getMetabolicsHistory;
          const updatedVitals = await dispatch(getHistoryAction({
            LoginUserID: userId,
            latest: false,
            token
          })).unwrap();
          setVitalsHistory(updatedVitals || []);
        } catch (error) {
          // Optionally show an error modal here
          console.error('Error deleting vital record:', error);
        }
      }
      // If cancelled, do nothing
    });
  };
  

  const fetchVitalsData = useCallback(async () => {
    if (!userId || !token) return;
    try {
      const getHistoryAction = isVitalsHistory ? getVitalsHistory : getMetabolicsHistory;
      const response = await dispatch(getHistoryAction({
        LoginUserID: userId,
        latest: false,
        token
      })).unwrap();
      setVitalsHistory(response || []);
    } catch (error) {
      console.error('Error fetching vitals:', error);
      setVitalsHistory([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, userId, token, isVitalsHistory]);

  useEffect(() => {
    if (show) {
      setLoading(true);
      fetchVitalsData();
    }
  }, [show, fetchVitalsData, selectedFamilyMember]);

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        dialogClassName="vitals-history-modal"
        style={{ zIndex: 1050 }}
        className="ViewHistory-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton style={{ borderBottom: "none", background: "var(--pmr-bg-soft)" }}>
          <Modal.Title className="text-capitalize" style={{ color: "#5a5875", fontWeight: 700, fontSize: 24 }}>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--pmr-bg-soft)", paddingTop: 0 }}>
          {loading ? (
            <StethoscopeLoading message="Loading..." />
          ) : (
            <div>
              <table className="table mb-0" style={{ background: "var(--pmr-bg-soft)" }}>
                <thead>
                  <tr>
                    <th>
                      <div className="header-col">
                        <div className="header-label-icon-row">
                          <span className="header-icon">
                            <MdCalendarToday size={18} color="#8bb6a2" />
                          </span>
                          <div className="header-label-unit">
                            <span className="header-label" style={{wordWrap:true}}>Date</span>
                            <span className="header-unit"></span>
                          </div>
                        </div>
                      </div>
                    </th>
                    {isVitalsHistory ? (
                      <>
                        <th>
                          <div className="header-col">
                            <div className="header-label-icon-row">
                              <span className="header-icon">
                                <FaHeartbeat size={18} color="#8bb6a2" />
                              </span>
                              <div className="header-label-unit">
                                <span className="header-label">Blood Pressure</span>
                                <span className="header-unit" style={{ color: "#ff9800" }}>(mmHg)</span>
                              </div>
                            </div>
                          </div>
                        </th>
                        <th>
                          <div className="header-col">
                            <div className="header-label-icon-row">
                              <span className="header-icon">
                                <FaHeart size={18} color="#8bb6a2" />
                              </span>
                              <div className="header-label-unit">
                                <span className="header-label">Heart Rate</span>
                                <span className="header-unit" style={{ color: "#ff9800" }}>(bpm)</span>
                              </div>
                            </div>
                          </div>
                        </th>
                      </>
                    ) : (
                      <>
                        <th>
                          <div className="header-col">
                            <div className="header-label-icon-row">
                              <span className="header-icon">
                                <FaTint size={18} color="#8bb6a2" />
                              </span>
                              <div className="header-label-unit">
                                <span className="header-label">Blood Sugar</span>
                                <span className="header-unit" style={{ color: "#ff9800" }}>(mg/dL)</span>
                              </div>
                            </div>
                          </div>
                        </th>
                        <th>
                          <div className="header-col">
                            <div className="header-label-icon-row">
                              <span className="header-icon">
                                <FaDatabase size={18} color="#8bb6a2" />
                              </span>
                              <div className="header-label-unit">
                                <span className="header-label">Weight</span>
                                <span className="header-unit" style={{ color: "#ff9800" }}>(lbs)</span>
                              </div>
                            </div>
                          </div>
                        </th>
                        <th>
                          <div className="header-col">
                            <div className="header-label-icon-row">
                              <span className="header-icon">
                                <FaUnderline size={18} color="#8bb6a2" />
                              </span>
                              <div className="header-label-unit">
                                <span className="header-label">Waist Size</span>
                                <span className="header-unit" style={{ color: "#ff9800" }}>(inches)</span>
                              </div>
                            </div>
                          </div>
                        </th>
                      </>
                    )}
                    <th>
                      <div className="header-col">
                        <div className="header-label-icon-row">
                          <span className="header-icon">
                            <FaEdit size={18} color="#8bb6a2" />
                          </span>
                          <div className="header-label-unit">
                            <span className="header-label">Edit</span>
                            <span className="header-unit" style={{ color: "#ff9800" }}>({isVitalsHistory ? 'Vitals' : 'Metabolics'})</span>
                          </div>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="header-col">
                        <div className="header-label-icon-row">
                          <span className="header-icon">
                            <FaTrash size={18} color="#8bb6a2" />
                          </span>
                          <div className="header-label-unit">
                            <span className="header-label">Delete</span>
                            <span className="header-unit" style={{ color: "#ff9800" }}>({isVitalsHistory ? 'Vitals' : 'Metabolics'})</span>
                          </div>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length === 0 ? (
                    <tr>
                      <td colSpan={isVitalsHistory ? 5 : 6} style={{ color: "#6c6a7a", fontWeight: 500, textAlign: "center" }}>
                        No {isVitalsHistory ? "vitals" : "metabolics"} history found
                      </td>
                    </tr>
                  ) : filteredHistory.map((row, idx) => (
                    <tr key={row.VitalID || idx}>
                      <td style={{ color: "#6c6a7a", fontWeight: 500 }}>
                        {formatHistoryDate(row.LastUpdated)}
                      </td>
                      {isVitalsHistory ? (
                        <>
                          <td style={{ color: "#6c6a7a", fontWeight: 500 }}>{row.BloodPressure_Systolic && row.BloodPressure_Diastolic ? `${row.BloodPressure_Systolic}/${row.BloodPressure_Diastolic}` : 'NA'}</td>
                          <td style={{ color: "#6c6a7a", fontWeight: 500 }}>{row.HeartRate || 'NA'}</td>
                        </>
                      ) : (
                        <>
                          <td style={{ color: "#6c6a7a", fontWeight: 500 }}>{row.BloodSugar || 'NA'}</td>
                          <td style={{ color: "#6c6a7a", fontWeight: 500 }}>{row.Weight || 'NA'}</td>
                          <td style={{ color: "#6c6a7a", fontWeight: 500 }}>{row.WaistSize || 'NA'}</td>
                        </>
                      )}
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <button
                            style={actionBtnStyle(isVitalsHistory ? "#4b8a7d" : "#ff9800", "#fff")}
                            onClick={() => isVitalsHistory ? handleEditVitals(row) : handleEditMetabolics(row)}
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <button
                            style={actionBtnStyle("#c8102e", "#fff")}
                            onClick={() => handleDelete(row)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-center mt-4">
            <Button
              variant="danger"
              className="vitals-history-close-btn text-capitalize"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
        <style>
          {`
            .vitals-history-modal .modal-content {
              width: auto;
              max-width: 95vw;
            }
            .vitals-history-modal .modal-body {
              padding: 1rem;
              overflow-x: auto;
            }
            .vitals-history-modal table {
              display: block;
              min-width: 100%;
              width: max-content;
            }
            .vitals-history-modal tbody {
              display: block;
              max-height: 350px;
              overflow-y: auto;
            }
            .vitals-history-modal thead, .vitals-history-modal tbody tr {
              display: table;
              width: 100%;
              table-layout: fixed;
              min-width: 700px;
            }
            .vitals-history-modal th, 
            .vitals-history-modal td {
              white-space: nowrap;
              text-overflow: ellipsis;
              overflow: hidden;
              vertical-align: middle;
              text-align: left;
              padding: 8px 4px;
              font-size: 14px;
              border: none !important;
              background: transparent; 
              min-width: 100px;
              max-width: 150px;
            }
            .vitals-history-modal thead tr {
              border-bottom: 2px solid #e0e0e0;
            }
            .vitals-history-modal tbody tr {
              border: none !important;
            }
            .vitals-history-modal td {
              border: none !important;
              text-align: center;
              vertical-align: middle;
            }
            .vitals-history-modal th {
              font-size: 14px;
              font-weight: 600;
              line-height: 1.2;
              text-transform: capitalize;
              padding-left: 12px;
              padding-right: 12px;
              border-right: 1.5px solid #e0e0e0;
              text-align: center;
              vertical-align: middle;
            }
            .vitals-history-modal th:last-child {
              border-right: none;
            }
            .vitals-history-modal thead tr {
              height: 48px;
            }
            .vitals-history-modal .header-col {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: center;
              min-width: 80px;
              padding: 0 2px;
              gap: 4px;
            }
            .vitals-history-modal .header-icon {
              display: flex;
              align-items: center;
              justify-content: center;
              background: #e6f0ed;
              border-radius: 50%;
              width: 28px;
              height: 28px;
              margin-bottom: 2px;
              flex-shrink: 0;
            }
            .vitals-history-modal .header-label-unit {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              min-width: 0;
            }
            .vitals-history-modal .header-label {
              font-size: 15px;
              font-weight: 600;
              color: #003366;
              line-height: 1.1;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            .vitals-history-modal .header-unit {
              font-size: 11px;
              font-weight: 500;
              line-height: 1;
              margin-top: 1px;
              margin-left: 0;
            }
            .modal.show.d-block {
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              min-height: 100vh;
            }
            .header-divider {
              border: none;
              border-bottom: 2px solid #e0e0e0;
              margin: 0;
            }
            .vitals-history-modal .header-label-icon-row {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: center;
              word-wrap: break-word;
              gap: 4px;
              flex-wrap: wrap;
            }
            .vitals-history-modal .modal-dialog {
              max-width: 95vw;
              width: auto;
              margin: 1.75rem auto;
            }
            .vitals-history-close-btn {
              border: none !important;
              border-radius: 8px !important;
              min-width: 90px !important;
              padding: 8px 0 !important;
              border-color: var(--pmr-danger) !important;
              background-color: var(--pmr-danger) !important;
              color: #fff !important;
              box-shadow: none !important;
              font-weight: 700 !important;
              cursor: pointer !important;
            }
            .vitals-history-close-btn:hover,
            .vitals-history-close-btn:focus,
            .vitals-history-close-btn:active {
              border-color: #c01236 !important;
              background-color: #c01236 !important;
              color: #fff !important;
              box-shadow: none !important;
            }
            .vitals-history-modal {
              z-index: 1050;
            }
            .vitals-history-modal.modal-backdrop {
              z-index: 1049;
            }
            .vitals-history-modal.modal-backdrop.show {
              opacity: 0.5;
            }
            .vitals-history-modal.modal-backdrop.show + .modal {
              filter: blur(2px);
              transition: filter 0.3s ease;
            }
            .my-custom-popup {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              }

              .custom-icon-center {
                display: flex !important;
                justify-content: center;
                align-items: center;
                margin: 1.25em auto 1.875em;
                font-size: 0.875em;
              }

              .custom-heading {
                text-transform: none;
                font-size: 1.5em;
                font-weight: 600;
                color: #333;
                margin-bottom: 0.5em;
              }
            .vitals-history-modal thead th {
                position: sticky;
                top: 0;
                background: #fafbfc;
                z-index: 2;
            }
            
            /* Responsive fixes for 125% resolution */
            @media screen and (min-resolution: 120dpi) {
              .vitals-history-modal .modal-dialog {
                max-width: 98vw;
                margin: 0.5rem auto;
              }
              .vitals-history-modal .modal-body {
                padding: 0.5rem;
              }
              .vitals-history-modal th, 
              .vitals-history-modal td {
                padding: 6px 3px;
                font-size: 13px;
              }
              .vitals-history-modal .header-label {
                font-size: 12px;
              }
              .vitals-history-modal .header-unit {
                font-size: 10px;
              }
              .vitals-history-modal .header-icon {
                width: 24px;
                height: 24px;
              }
              .vitals-history-modal thead, .vitals-history-modal tbody tr {
                min-width: 650px;
              }
            }
            
            /* Additional responsive breakpoints */
            @media (max-width: 1400px) {
              .vitals-history-modal .modal-dialog {
                max-width: 98vw;
              }
              .vitals-history-modal thead, .vitals-history-modal tbody tr {
                min-width: 650px;
              }
            }
            
            @media (max-width: 1200px) {
              .vitals-history-modal .modal-dialog {
                max-width: 99vw;
              }
              .vitals-history-modal thead, .vitals-history-modal tbody tr {
                min-width: 650px;
              }
            }
          `}
        </style>
      </Modal>

      <VitalsMetabolicsModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        rowData={editData}
        isVitals={isVitalsEdit}
        selectedFamilyMember={selectedFamilyMember}
      />

      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => { setShowSuccessModal(false); setSuccessMessage(''); }}
        message={successMessage}
      />
    </>
  );
};

export default ViewHistory;
